import { useState } from 'react';

import { PlusIcon, XIcon } from 'lucide-react';

import { ApiError } from '@mds/shared/lib/api';
import type { AdminCustomer } from '@mds/shared/types/customer';

import Button from '@/components/ui/button';
import Chip from '@/components/ui/chip';
import { inputCls } from '@/components/ui/field';
import * as customers from '@/customers/customers.api';
import { TAG_POOL } from '@/data/customers';

/**
 * A customer's tags, with an × on each and an "Add tag" button when the viewer
 * may manage customers.
 *
 * Each change saves straight away by PATCHing the whole tag list — the API has
 * no add-one / remove-one endpoint — and hands the saved record back up.
 */
export default function CustomerTags({
  customer,
  editable,
  onSaved,
}: {
  customer: AdminCustomer;
  editable: boolean;
  onSaved: (saved: AdminCustomer) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const save = async (tags: string[]) => {
    setBusy(true);
    setProblem(null);
    try {
      onSaved(await customers.updateCustomer(customer.id, { tags }));
      return true;
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : 'The tags did not save. Try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (raw: string) => {
    const next = raw.trim();
    if (!next) return;
    // Case-insensitive, or the same tag arrives twice in two spellings and the
    // filters treat them as different things.
    if (customer.tags.some((t) => t.toLowerCase() === next.toLowerCase())) {
      setDraft('');
      return;
    }
    if (await save([...customer.tags, next])) {
      setDraft('');
      setAdding(false);
    }
  };

  const remove = (tag: string) => void save(customer.tags.filter((t) => t !== tag));

  const suggestions = TAG_POOL.filter(
    (t) => !customer.tags.some((have) => have.toLowerCase() === t.toLowerCase()),
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Chip className="border border-brand-edge/40 bg-brand-wash text-brand-strong">
          {customer.category.name}
        </Chip>
        {customer.tags.map((t) => (
          <Chip key={t}>
            {t}
            {editable && (
              <button
                type="button"
                onClick={() => remove(t)}
                disabled={busy}
                aria-label={`Remove tag ${t}`}
                title={`Remove tag ${t}`}
                className="-my-1 ml-1.5 -mr-1.5 inline-flex size-6 items-center justify-center rounded-full text-ink-muted hover:bg-brand-wash hover:text-brand-strong focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color) disabled:opacity-50"
              >
                <XIcon className="size-3.5" strokeWidth={2.6} aria-hidden="true" />
              </button>
            )}
          </Chip>
        ))}
        {editable && !adding && (
          <Button variant="quiet" className="h-9 px-3 text-sm" onClick={() => setAdding(true)}>
            <PlusIcon className="size-4" strokeWidth={2.4} aria-hidden="true" />
            Add tag
          </Button>
        )}
      </div>

      {editable && adding && (
        <div className="flex flex-col gap-2">
          <div className="flex items-stretch gap-2">
            <input
              autoFocus
              className={inputCls}
              placeholder="New tag"
              aria-label="New tag"
              value={draft}
              disabled={busy}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  // Close the input, not the whole record.
                  e.stopPropagation();
                  setAdding(false);
                  setDraft('');
                }
                if (e.key !== 'Enter') return;
                e.preventDefault();
                void add(draft);
              }}
            />
            <Button
              className="h-auto"
              onClick={() => void add(draft)}
              disabled={busy || !draft.trim()}
            >
              {busy ? 'Saving…' : 'Add'}
            </Button>
            <Button
              variant="quiet"
              className="h-auto"
              disabled={busy}
              onClick={() => {
                setAdding(false);
                setDraft('');
              }}
            >
              Cancel
            </Button>
          </div>
          {suggestions.length > 0 && (
            <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
              {suggestions.map((t) => (
                <li key={t}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void add(t)}
                    className="rounded-full border border-hairline bg-surface px-3 py-1 text-md text-ink-muted transition-colors hover:border-brand-edge hover:text-brand-text focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color) disabled:opacity-50"
                  >
                    + {t}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {problem && <p className="m-0 text-base text-warn-text">{problem}</p>}
    </div>
  );
}
